import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/hooks/useRealtime";
import { useAgentCallCenter } from "@/hooks/useAgentCallCenter";
import { ChatThread } from "@/components/chat/ChatThread";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { CallOverlay } from "@/components/chat/CallOverlay";
import { IncomingCallStack } from "@/components/chat/IncomingCallStack";
import { WorkspaceSettings } from "@/components/dashboard/WorkspaceSettings";
import { initials, avatarColor, formatTime, formatDayLabel } from "@/lib/format";
import { playMessageDing, primeAudio } from "@/lib/sound";
import { LANGS, langLabel } from "@/lib/langs";
import { LOGO_URL } from "@/lib/brand";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Search, Phone, MoreVertical, ArrowLeft, Settings, LogOut, Info, CheckCircle2,
  RotateCcw, Image as ImageIcon, FileText, Mic, MessageSquare, Mail, Globe, UserCheck,
  MapPin, Plus, Check, X, Tag, Languages, Eye,
} from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "mine", label: "Assigned to me" },
];

const STATUS_STYLE = {
  open: "bg-[#2ac67b]/15 text-[#2ac67b] border border-[#2ac67b]/30",
  pending: "bg-amber-400/15 text-amber-300 border border-amber-400/30",
  closed: "bg-white/10 text-white/50 border border-white/10",
};

function Avatar({ name, size = 44 }) {
  const [bg, fg] = avatarColor(name || "");
  return (
    <div className="flex flex-shrink-0 items-center justify-center rounded-full font-semibold" style={{ width: size, height: size, background: bg, color: fg, fontSize: size * 0.4 }}>
      {initials(name)}
    </div>
  );
}

function listTime(iso) {
  if (!iso) return "";
  const label = formatDayLabel(iso);
  return label === "Today" ? formatTime(iso) : label;
}

function PreviewIcon({ type }) {
  const cls = "mr-1 inline h-3.5 w-3.5 -translate-y-px text-white/40";
  if (type === "image") return <ImageIcon className={cls} aria-hidden="true" />;
  if (type === "file") return <FileText className={cls} aria-hidden="true" />;
  if (type === "voice") return <Mic className={cls} aria-hidden="true" />;
  return null;
}

export default function AgentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [typingVisitor, setTypingVisitor] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [viewers, setViewers] = useState({}); // convId -> { agentId: name } (other agents present)
  const typingClearRef = useRef(null);
  const callHandlerRef = useRef(null);
  const openConvRef = useRef(null);
  const activeIdRef = useRef(null);
  const loadSeqRef = useRef(0);
  const wasConnectedRef = useRef(false);
  const prevViewRef = useRef(null);

  const active = conversations.find((c) => c.id === activeId) || null;
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  // Which OTHER agents are currently looking at a conversation (collision hint).
  const viewersOf = useCallback((convId) => {
    const v = viewers[convId] || {};
    return Object.entries(v).filter(([id]) => id !== user?.id).map(([, name]) => name);
  }, [viewers, user]);

  const upsertConversation = useCallback((conv) => {
    setConversations((prev) => {
      const idx = prev.findIndex((c) => c.id === conv.id);
      let next;
      if (idx >= 0) { next = [...prev]; next[idx] = { ...next[idx], ...conv }; }
      else next = [conv, ...prev];
      return next.sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
    });
  }, []);

  const { send, connected } = useRealtime({
    token: getToken(),
    enabled: !!user,
    handlers: {
      onMessage: (m) => {
        // Only ding for chats that are mine or unassigned — not the whole
        // workspace (avoids every agent hearing every customer's message).
        if (m.sender_type === "visitor" && m.conversation_id !== activeId) {
          const conv = conversations.find((c) => c.id === m.conversation_id);
          const mine = !conv || !conv.assigned_agent_id || conv.assigned_agent_id === user?.id;
          if (mine) playMessageDing();
        }
        if (m.conversation_id === activeId) setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
      },
      onConversationUpdate: (c) => { if (c.id === activeId) c = { ...c, unread_count: 0 }; upsertConversation(c); },
      onMessageUpdate: (u) => setMessages((prev) => prev.map((m) => (m.id === u.id ? { ...m, ...u } : m))),
      onPresence: (d) => {
        setViewers((prev) => {
          const conv = { ...(prev[d.conversation_id] || {}) };
          if (d.viewing) conv[d.agent_id] = d.agent_name || "Agent";
          else delete conv[d.agent_id];
          return { ...prev, [d.conversation_id]: conv };
        });
      },
      onTyping: (d) => {
        if (d.conversation_id === activeId && d.sender_type === "visitor") {
          setTypingVisitor(!!d.is_typing);
          clearTimeout(typingClearRef.current);
          if (d.is_typing) typingClearRef.current = setTimeout(() => setTypingVisitor(false), 3000);
        }
      },
      onSignal: (d) => callHandlerRef.current && callHandlerRef.current(d),
    },
  });

  const call = useAgentCallCenter({
    send,
    selfName: user?.name,
    onAccept: (convId) => openConvRef.current && openConvRef.current(convId),
  });
  useEffect(() => { callHandlerRef.current = call.processSignal; }, [call.processSignal]);

  // Broadcast which conversation this agent is viewing, so teammates see a
  // "also here" hint and don't answer the same customer twice.
  useEffect(() => {
    if (prevViewRef.current && prevViewRef.current !== activeId) {
      send({ type: "viewing", conversation_id: prevViewRef.current, viewing: false });
    }
    if (activeId) send({ type: "viewing", conversation_id: activeId, viewing: true });
    prevViewRef.current = activeId;
  }, [activeId, send]);

  useEffect(() => {
    api.get("/workspace").then(({ data }) => setWorkspace(data)).catch(() => {});
    api.get("/agents").then(({ data }) => setAgents(data)).catch(() => {});
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lock the page so only the inner panes scroll — the header, search and
  // composer never move (critical on mobile).
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const prevH = html.style.overflow, prevB = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => { html.style.overflow = prevH; body.style.overflow = prevB; };
  }, []);

  // Hardware / browser back keeps you inside the app: close an open sheet, then
  // return from a conversation to the list — instead of leaving the page.
  useEffect(() => {
    const onPop = () => {
      if (settingsOpen) { setSettingsOpen(false); return; }
      if (infoOpen) { setInfoOpen(false); return; }
      if (activeIdRef.current) { setActiveId(null); return; }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [settingsOpen, infoOpen]);

  // Push a history entry when a sheet opens (rising edge) so back closes it.
  const prevInfoRef = useRef(false);
  useEffect(() => { if (infoOpen && !prevInfoRef.current) { try { window.history.pushState(null, ""); } catch {} } prevInfoRef.current = infoOpen; }, [infoOpen]);
  const prevSettingsRef = useRef(false);
  useEffect(() => { if (settingsOpen && !prevSettingsRef.current) { try { window.history.pushState(null, ""); } catch {} } prevSettingsRef.current = settingsOpen; }, [settingsOpen]);

  // Unlock audio on the agent's first interaction so notification/ring sounds
  // are allowed to play (browser autoplay policy).
  useEffect(() => {
    const prime = () => { primeAudio(); window.removeEventListener("pointerdown", prime); window.removeEventListener("keydown", prime); };
    window.addEventListener("pointerdown", prime);
    window.addEventListener("keydown", prime);
    return () => { window.removeEventListener("pointerdown", prime); window.removeEventListener("keydown", prime); };
  }, []);

  const loadConversations = async () => {
    try { const { data } = await api.get("/conversations"); setConversations(data); } catch {}
  };

  // Sequence-guarded so a slow response for a previously opened conversation
  // can never overwrite the messages of the one currently on screen.
  const refreshMessages = async (id) => {
    const seq = ++loadSeqRef.current;
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      if (seq === loadSeqRef.current && activeIdRef.current === id) setMessages(data);
    } catch {}
  };

  const openConversation = async (id) => {
    // Entering the conversation view (from the list): add a history entry so the
    // hardware back button returns to the list instead of leaving the app.
    if (!activeIdRef.current) { try { window.history.pushState(null, ""); } catch {} }
    activeIdRef.current = id;
    setActiveId(id);
    setTypingVisitor(false);
    setInfoOpen(false);
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)));
    await refreshMessages(id);
  };
  openConvRef.current = openConversation;

  // After a WebSocket drop, re-sync everything that may have arrived while
  // offline (list ordering, unread counts, and the open thread).
  useEffect(() => {
    if (!connected) return;
    if (wasConnectedRef.current) {
      loadConversations();
      if (activeIdRef.current) refreshMessages(activeIdRef.current);
    }
    wasConnectedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  const sendMessage = async (body) => {
    try {
      const { data } = await api.post(`/conversations/${activeId}/messages`, body);
      setMessages((prev) => (prev.some((x) => x.id === data.id) ? prev : [...prev, data]));
      return true;
    } catch {
      return false;
    }
  };

  const sendTyping = (isTyping) => send({ type: "typing", conversation_id: activeId, sender: user.name, is_typing: isTyping });

  const setStatus = async (status) => {
    try { const { data } = await api.patch(`/conversations/${activeId}`, { status }); upsertConversation(data); } catch {}
  };
  const assignToMe = async () => {
    try { const { data } = await api.patch(`/conversations/${activeId}`, { assigned_agent_id: user.id }); upsertConversation(data); } catch {}
  };
  const assignTo = async (agentId) => {
    try { const { data } = await api.patch(`/conversations/${activeId}`, { assigned_agent_id: agentId }); upsertConversation(data); } catch {}
  };
  const tagPalette = workspace?.conversation_tags || [];
  const tagColor = (label) => tagPalette.find((t) => t.label === label)?.color || "#6b7280";
  const toggleTag = async (label) => {
    if (!active) return;
    const current = active.tags || [];
    const next = current.includes(label) ? current.filter((t) => t !== label) : [...current, label];
    try { const { data } = await api.patch(`/conversations/${active.id}`, { tags: next }); upsertConversation(data); } catch {}
  };
  const setCustomerLang = async (lang) => {
    if (!active) return;
    try { const { data } = await api.patch(`/conversations/${active.id}`, { customer_lang: lang }); upsertConversation(data); } catch {}
  };
  const voiceTranslate = async (msg) => {
    try {
      const { data } = await api.post(`/conversations/${activeId}/voice-translate`, {
        message_id: msg.id, target: workspace?.agent_language || "hi-IN", tts: true,
      });
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, voice_translation: data } : m)));
      return data;
    } catch { return null; }
  };
  const doLogout = async () => { await logout(); navigate("/login"); };

  const filtered = conversations
    .filter((c) => {
      if (filter === "unread") return (c.unread_count || 0) > 0;
      if (filter === "mine") return c.assigned_agent_id === user?.id;
      return true;
    })
    .filter((c) => (search ? c.visitor_name?.toLowerCase().includes(search.toLowerCase()) : true));

  const InfoContent = active && (
    <div className="flex flex-col items-center gap-4 p-6">
      <Avatar name={active.visitor_name} size={88} />
      <div className="text-center">
        <div className="text-lg font-semibold text-white">{active.visitor_name}</div>
        <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLE[active.status]}`}>{active.status}</span>
      </div>
      <div className="w-full space-y-3 border-t border-white/10 pt-4 text-sm">
        {active.visitor_phone && <a href={`tel:${active.visitor_phone}`} className="flex items-center gap-3 text-white/60 transition-colors hover:text-[#2ac67b]" data-testid="info-visitor-phone"><Phone className="h-4 w-4" aria-hidden="true" /> {active.visitor_phone}</a>}
        {active.visitor_email && <a href={`mailto:${active.visitor_email}`} className="flex items-center gap-3 text-white/60 transition-colors hover:text-[#2ac67b]" data-testid="info-visitor-email"><Mail className="h-4 w-4" aria-hidden="true" /> {active.visitor_email}</a>}
        {active.visitor_location && <div className="flex items-center gap-3 text-white/60" data-testid="info-visitor-location"><MapPin className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> {active.visitor_location}</div>}
        {active.page_url && <div className="flex items-start gap-3 text-white/60"><Globe className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" /> <span className="break-all font-mono-x text-xs">{active.page_url}</span></div>}
        <div className="flex items-center gap-3 text-white/60"><UserCheck className="h-4 w-4" aria-hidden="true" /> {active.assigned_agent_name || "Unassigned"}</div>
      </div>

      <div className="w-full">
        <div className="mb-1.5 flex items-center justify-between">
          <label className="flex items-center gap-1.5 font-mono-x text-[10px] uppercase tracking-[0.15em] text-white/40"><Tag className="h-3 w-3" aria-hidden="true" /> Tags</label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-white/60 hover:bg-white/5" data-testid="add-tag-button"><Plus className="h-3 w-3" aria-hidden="true" /> Add</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
              {tagPalette.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No tags defined. Add them in Settings.</div>}
              {tagPalette.map((t) => {
                const on = (active.tags || []).includes(t.label);
                return (
                  <DropdownMenuItem key={t.label} onSelect={(e) => { e.preventDefault(); toggleTag(t.label); }} data-testid={`tag-option-${t.label}`}>
                    <span className="mr-2 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: t.color }} />
                    <span className="flex-1">{t.label}</span>
                    {on && <Check className="ml-2 h-3.5 w-3.5 text-[#2ac67b]" aria-hidden="true" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(active.tags || []).length === 0 && <span className="text-xs text-white/30">No tags yet</span>}
          {(active.tags || []).map((label) => (
            <span key={label} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: `${tagColor(label)}22`, color: tagColor(label), border: `1px solid ${tagColor(label)}55` }} data-testid={`tag-chip-${label}`}>
              {label}
              <button onClick={() => toggleTag(label)} className="opacity-60 transition-opacity hover:opacity-100" aria-label={`Remove ${label}`}><X className="h-3 w-3" aria-hidden="true" /></button>
            </span>
          ))}
        </div>
      </div>
      {workspace?.translation_enabled && (
        <div className="w-full">
          <label className="mb-1 flex items-center gap-1.5 font-mono-x text-[10px] uppercase tracking-[0.15em] text-white/40"><Languages className="h-3 w-3" aria-hidden="true" /> Customer language</label>
          <select
            value={active.customer_lang || ""}
            onChange={(e) => setCustomerLang(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-[#0d1a16] px-3 py-2 text-sm text-white outline-none focus:border-[#2ac67b]"
            data-testid="customer-lang-select"
          >
            <option value="">Auto-detect</option>
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
      )}
      <div className="w-full">
        <label className="mb-1 block font-mono-x text-[10px] uppercase tracking-[0.15em] text-white/40">Assign to</label>
        <select
          value={active.assigned_agent_id || ""}
          onChange={(e) => (e.target.value ? assignTo(e.target.value) : null)}
          className="w-full rounded-lg border border-white/10 bg-[#0d1a16] px-3 py-2 text-sm text-white outline-none focus:border-[#2ac67b]"
          data-testid="assign-agent-select"
        >
          <option value="">Unassigned</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.name}{a.id === user?.id ? " (me)" : ""}{a.role === "business_admin" ? " · admin" : ""}</option>
          ))}
        </select>
      </div>
      <div className="mt-2 w-full space-y-2">
        {!active.assigned_agent_id && (
          <button onClick={assignToMe} className="w-full rounded-lg bg-[#2ac67b] py-2 text-sm font-medium text-black transition-all hover:shadow-[0_0_18px_rgba(42,198,123,0.4)]" data-testid="assign-to-me-button">Assign to me</button>
        )}
        {active.status !== "closed" ? (
          <button onClick={() => setStatus("closed")} className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-sm font-medium text-white/70 hover:bg-white/5" data-testid="close-conversation-button"><CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Mark as resolved</button>
        ) : (
          <button onClick={() => setStatus("open")} className="flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-sm font-medium text-white/70 hover:bg-white/5" data-testid="reopen-conversation-button"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Reopen</button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#050a08] text-white supports-[height:100dvh]:h-[100dvh]">
      {!connected && wasConnectedRef.current && (
        <div className="fixed left-1/2 top-2 z-[80] -translate-x-1/2 rounded-full border border-amber-400/30 bg-[#1a1405]/90 px-4 py-1.5 text-xs font-medium text-amber-300 shadow-lg backdrop-blur" data-testid="reconnecting-banner">
          Reconnecting…
        </div>
      )}
      {/* Conversation list */}
      <aside className={`${activeId ? "hidden md:flex" : "flex"} min-h-0 w-full flex-col overflow-hidden border-r border-white/5 bg-[#070f0c] md:w-[320px] lg:w-[380px]`}>
        <div className="flex flex-shrink-0 items-center justify-between border-b border-white/5 px-4 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="SuperChat" className="h-10 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setSettingsOpen(true)} className="rounded-full p-2 text-white/60 hover:bg-white/5" data-testid="open-settings-button" aria-label="Settings"><Settings className="h-5 w-5" aria-hidden="true" /></button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full p-2 text-white/60 hover:bg-white/5" data-testid="dashboard-menu-button" aria-label="Menu"><MoreVertical className="h-5 w-5" aria-hidden="true" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-medium">{user?.name}</div>
                  <div className="font-mono-x text-xs text-muted-foreground">{user?.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-[#ff6b88]"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex-shrink-0 px-3 py-2">
          <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
            <Search className="h-4 w-4 flex-shrink-0 text-white/40" aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations" className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/30" data-testid="conversation-search-input" />
          </div>
          <div className="mt-2 flex gap-1.5">
            {FILTERS.map((f) => {
              const count = f.key === "unread" ? conversations.filter((c) => (c.unread_count || 0) > 0).length : 0;
              return (
                <button key={f.key} onClick={() => setFilter(f.key)} className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors ${filter === f.key ? "bg-[#2ac67b] text-black" : "bg-white/5 text-white/60 hover:bg-white/10"}`} data-testid={`filter-${f.key}`}>
                  {f.label}
                  {f.key === "unread" && count > 0 && (
                    <span className={`flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold ${filter === f.key ? "bg-black/25 text-black" : "bg-[#2ac67b] text-black"}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="sc-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-white/40">
              <MessageSquare className="h-8 w-8 opacity-40" aria-hidden="true" /> No conversations here yet.
            </div>
          )}
          {filtered.map((c) => (
            <button key={c.id} onClick={() => openConversation(c.id)} className={`flex w-full items-center gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/[0.04] ${activeId === c.id ? "bg-white/[0.06]" : ""}`} data-testid={`conversation-item-${c.id}`}>
              <Avatar name={c.visitor_name} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate font-medium text-white">{c.visitor_name}</span>
                    {c.customer_lang && <span className="flex-shrink-0 rounded bg-[#2ac67b]/15 px-1 text-[9px] font-semibold text-[#2ac67b]" title="Detected language">{langLabel(c.customer_lang)}</span>}
                    {viewersOf(c.id).length > 0 && <Eye className="h-3 w-3 flex-shrink-0 text-amber-300" aria-hidden="true" title={`${viewersOf(c.id).join(", ")} here`} />}
                  </div>
                  <span className="flex-shrink-0 font-mono-x text-[10px] text-white/40">{listTime(c.last_message_at)}</span>
                </div>
                {c.visitor_phone && (
                  <div className="flex items-center gap-1 truncate font-mono-x text-[11px] text-[#2ac67b]/80" data-testid={`list-phone-${c.id}`}>
                    <Phone className="h-3 w-3 flex-shrink-0" aria-hidden="true" /> {c.visitor_phone}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm text-white/50"><PreviewIcon type={c.last_message_type} />{c.last_message || "New conversation"}</span>
                  {c.unread_count > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#2ac67b] px-1.5 text-[11px] font-bold text-black shadow-[0_0_10px_rgba(42,198,123,0.5)]" data-testid={`unread-badge-${c.id}`}>{c.unread_count}</span>
                  )}
                </div>
                {(c.tags || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(c.tags || []).slice(0, 4).map((label) => (
                      <span key={label} className="rounded px-1.5 py-0.5 text-[9px] font-semibold" style={{ background: `${tagColor(label)}22`, color: tagColor(label) }}>{label}</span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Chat panel */}
      <main className={`${activeId ? "flex" : "hidden md:flex"} relative min-h-0 min-w-0 flex-1 flex-col`}>
        {!active ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 web3-chat-canvas text-center">
            <img src={LOGO_URL} alt="SuperChat" className="h-20 w-auto object-contain opacity-90 sc-float" />
            <div>
              <h2 className="font-display text-2xl font-bold text-white">SuperChat Console</h2>
              <p className="mt-1 max-w-sm text-sm text-white/40">Select a conversation to start replying. Messages, voice calls and files sync in real time.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-white/5 bg-[#091310]/80 px-3 py-2.5 backdrop-blur-xl sm:gap-3 sm:px-4" style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}>
              <button onClick={() => window.history.back()} className="-ml-1 rounded-full p-2 text-white/60 hover:bg-white/5 md:hidden" data-testid="chat-back-button" aria-label="Back"><ArrowLeft className="h-5 w-5" aria-hidden="true" /></button>
              <button onClick={() => setInfoOpen(true)} className="flex min-w-0 flex-1 items-center gap-3 text-left xl:cursor-default" data-testid="chat-header-info">
                <Avatar name={active.visitor_name} size={40} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-semibold text-white">{active.visitor_name}</span>
                    {active.customer_lang && (
                      <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-[#2ac67b]/15 px-2 py-0.5 text-[10px] font-semibold text-[#2ac67b]" title="Detected native language" data-testid="header-lang-badge">
                        <Languages className="h-3 w-3" aria-hidden="true" /> {langLabel(active.customer_lang)}
                      </span>
                    )}
                    {viewersOf(active.id).length > 0 && (
                      <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300" title="Another agent is in this chat" data-testid="header-viewing-badge">
                        <Eye className="h-3 w-3" aria-hidden="true" /> {viewersOf(active.id).join(", ")} here
                      </span>
                    )}
                  </div>
                  <div className="truncate text-xs text-white/45">{typingVisitor ? <span className="text-[#2ac67b]">typing…</span> : active.visitor_phone || active.visitor_email || "Visitor"}</div>
                </div>
              </button>
              <button onClick={() => call.startCall(activeId, active.visitor_name)} disabled={!!call.active} className="rounded-full p-2 text-white/60 transition-colors hover:bg-white/5 hover:text-[#2ac67b] disabled:opacity-40" data-testid="start-call-button" aria-label="Start voice call"><Phone className="h-5 w-5" aria-hidden="true" /></button>
              <button onClick={() => setInfoOpen(true)} className="rounded-full p-2 text-white/60 hover:bg-white/5 xl:hidden" data-testid="open-info-button" aria-label="Contact info"><Info className="h-5 w-5" aria-hidden="true" /></button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-full p-2 text-white/60 hover:bg-white/5" data-testid="chat-menu-button" aria-label="Conversation menu"><MoreVertical className="h-5 w-5" aria-hidden="true" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {active.status !== "closed" ? (
                    <DropdownMenuItem onClick={() => setStatus("closed")} data-testid="menu-close-conversation"><CheckCircle2 className="mr-2 h-4 w-4" /> Mark as resolved</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setStatus("open")} data-testid="menu-reopen-conversation"><RotateCcw className="mr-2 h-4 w-4" /> Reopen</DropdownMenuItem>
                  )}
                  {!active.assigned_agent_id && <DropdownMenuItem onClick={assignToMe}><UserCheck className="mr-2 h-4 w-4" /> Assign to me</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <ChatThread messages={messages} isMine={(m) => m.sender_type === "agent"} typingLabel={typingVisitor ? "typing" : null} surface="dark" showEncryptionNotice={false} onVoiceTranslate={workspace?.translation_enabled ? voiceTranslate : undefined} />

            <div className="flex-shrink-0 border-t border-white/5 bg-[#0d1a16]" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
              <ChatComposer onSend={sendMessage} onTyping={sendTyping} accent="#2ac67b" surface="dark" disabled={active.status === "closed"} />
            </div>
          </>
        )}
      </main>

      {/* Persistent info panel on wide screens */}
      {active && <aside className="hidden w-[340px] border-l border-white/5 bg-[#070f0c] xl:block">{InfoContent}</aside>}

      <Sheet open={infoOpen} onOpenChange={setInfoOpen}>
        <SheetContent className="w-full overflow-y-auto border-white/10 bg-[#091310] text-white sm:max-w-sm" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }} data-testid="info-sheet">
          <SheetHeader><SheetTitle className="text-white">Contact info</SheetTitle></SheetHeader>
          {InfoContent}
        </SheetContent>
      </Sheet>

      <WorkspaceSettings open={settingsOpen} onOpenChange={setSettingsOpen} workspace={workspace} setWorkspace={setWorkspace} user={user} />

      <CallOverlay
        call={call.active
          ? { callState: call.active.state, peerName: call.active.callerName, muted: call.active.muted, seconds: call.active.seconds, netState: call.active.netState, level: call.active.level, toggleMute: call.toggleMute, endCall: () => call.endCall() }
          : { callState: "idle" }}
        label={call.active?.callerName}
        accent="#2ac67b"
        fixed
      />
      <IncomingCallStack incoming={call.incoming} onAccept={call.acceptCall} onReject={call.rejectCall} />
    </div>
  );
}
